/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { Button, Collapse } from 'reactstrap';
import isEmpty from 'lodash/isEmpty';

import {
  COMPONENT_TEMPLATE,
  COMPONENT_TEMPLATE_CLOSED,
  PAGE_TEMPLATES,
  PAGE_TEMPLATES_CLOSED,
  SEARCH_TEMPLATE,
  SEARCH_TEMPLATE_CLOSED
} from 'app/App/const';
import PageName from 'app/PageName/container';
import Tree from 'app/Tree/container';
import { noop } from 'lib/noop';
import { Arrow } from 'ui/Components';

import SearchTab from './SearchTab';
import ComponentsTab from './ComponentsTab';
import styles from './Explorer.css';

/**
 * render the Explorer component
 * @param {object} props - the props passed to this component
 * @returns {React.Component} the Explorer component
 */
export default class Explorer extends Component {
  static propTypes = {
    addFilter: propTypes.func.isRequired,
    addNewComponentToPage: propTypes.func.isRequired,
    applicationMode: propTypes.string.isRequired,
    clearSearch: propTypes.func,
    cwdSiteRoot: propTypes.string.isRequired,
    deleteFilter: propTypes.func.isRequired,
    getSearchResults: propTypes.func.isRequired,
    canEditAtLeastOneComponent: propTypes.bool,
    checkStatusAndDuplicatePage: propTypes.func.isRequired,
    checkStatusAndRemoveActivePage: propTypes.func.isRequired,
    checkStatusAndToggleAddPage: propTypes.func.isRequired,
    checkStatusAndToggleCreateMasterComponent: propTypes.func.isRequired,
    checkStatusAndVisitPage: propTypes.func.isRequired,
    editableComponentsList: propTypes.array,
    contractMap: propTypes.object.isRequired,
    createNewUnsavedPage: propTypes.func.isRequired,
    filters: propTypes.arrayOf(
      propTypes.shape({
        text: propTypes.string,
        key: propTypes.string,
        val: propTypes.oneOfType([propTypes.string, propTypes.array])
      })
    ),
    isPageSelected: propTypes.bool,
    isReadonlyFolder: propTypes.bool,
    moveContent: propTypes.func.isRequired,
    pageTemplateName: propTypes.string.isRequired,
    pageType: propTypes.string.isRequired,
    showAddPageDialog: propTypes.bool,
    showCreateMasterComponentDialog: propTypes.bool,
    switchApplicationMode: propTypes.func.isRequired,
    toggleAddPage: propTypes.func.isRequired,
    toggleCreateMasterComponent: propTypes.func.isRequired,
    visitFolder: propTypes.func.isRequired
  };

  static defaultProps = {
    canEditAtLeastOneComponent: true,
    clearSearch: noop,
    editableComponentsList: [],
    filters: [],
    isPageSelected: false,
    isReadonlyFolder: true,
    showAddPageDialog: false,
    showCreateMasterComponentDialog: false
  };

  constructor(props) {
    super(props);

    this.state = {
      isExplorerOpen: true
    };

    this.handleComponentsTemplateClick = this.handleComponentsTemplateClick.bind(this);
    this.handlePageTemplateClick = this.handlePageTemplateClick.bind(this);
    this.handleSearchTemplateClick = this.handleSearchTemplateClick.bind(this);
    this.onSearchResultClick = this.onSearchResultClick.bind(this);
    this.toggleExplorer = this.toggleExplorer.bind(this);
  }

  /**
   * switch application to 'components' mode
   */
  handleComponentsTemplateClick() {
    const { applicationMode, clearSearch, switchApplicationMode } = this.props;
    switchApplicationMode(
      applicationMode === COMPONENT_TEMPLATE ? COMPONENT_TEMPLATE_CLOSED : COMPONENT_TEMPLATE
    );
    clearSearch();
  }

  /**
   * switch application to 'pages' mode
   */
  handlePageTemplateClick() {
    const { applicationMode, clearSearch, switchApplicationMode } = this.props;
    switchApplicationMode(
      applicationMode === PAGE_TEMPLATES ? PAGE_TEMPLATES_CLOSED : PAGE_TEMPLATES
    );
    clearSearch();
  }

  /**
   * switch application to 'search' mode
   */
  handleSearchTemplateClick() {
    const { applicationMode, switchApplicationMode } = this.props;
    switchApplicationMode(
      applicationMode === SEARCH_TEMPLATE ? SEARCH_TEMPLATE_CLOSED : SEARCH_TEMPLATE
    );
  }

  onSearchResultClick(result) {
    const { checkStatusAndVisitPage, visitFolder } = this.props;
    const { DocType, Id, Version } = result;

    if (DocType === 'folder') {
      visitFolder(Id);
    } else {
      checkStatusAndVisitPage(Id, Version);
    }
  }

  /**
   * toggle the isExplorerOpen ui state
   */
  toggleExplorer() {
    this.setState(ps => ({ isExplorerOpen: !ps.isExplorerOpen }));
  }

  render() {
    const {
      addFilter,
      addNewComponentToPage,
      applicationMode,
      canEditAtLeastOneComponent,
      checkStatusAndDuplicatePage,
      checkStatusAndRemoveActivePage,
      checkStatusAndToggleAddPage,
      checkStatusAndToggleCreateMasterComponent,
      checkStatusAndVisitPage,
      editableComponentsList,
      createNewUnsavedPage,
      cwdSiteRoot,
      deleteFilter,
      filters,
      getSearchResults,
      isPageSelected,
      isReadonlyFolder,
      moveContent,
      pageTemplateName,
      pageType,
      showAddPageDialog,
      showCreateMasterComponentDialog,
      toggleAddPage,
      toggleCreateMasterComponent,
      visitFolder,
      contractMap
    } = this.props;
    const { isExplorerOpen } = this.state;

    const wrapperClass = classnames('border', 'border-secondary', styles.wrapper, {
      'border-right-0': !isExplorerOpen,
      pane: isExplorerOpen,
      'w-0': !isExplorerOpen,
      'w-100': isExplorerOpen
    });

    return (
      <div className={wrapperClass}>
        <Button
          color="light"
          className={classnames('border-right border-secondary', styles.toggleButton)}
          onClick={this.toggleExplorer}
        >
          <Arrow facing={isExplorerOpen ? 'left' : 'right'} theme={styles.toggleSideArrow} />
        </Button>
        <div className={isExplorerOpen ? styles.open : styles.closed}>
          {isExplorerOpen && (
            <Fragment>
              <div
                className={classnames(styles.pageHeader, 'p-4')}
                onClick={this.handlePageTemplateClick}
                data-test-id="explorer-page-button"
              >
                Page
                <Arrow
                  facing={applicationMode === PAGE_TEMPLATES ? 'down' : 'right'}
                  color="white"
                  theme={styles.toggleArrow}
                />
              </div>
              <PageName removeActivePage={checkStatusAndRemoveActivePage} />
              <Collapse
                className={classnames('overflow-y', styles.details)}
                isOpen={applicationMode === PAGE_TEMPLATES}
              >
                {applicationMode === PAGE_TEMPLATES && (
                  <div className="d-flex flex-column pl-3 pr-2 pb-4">
                    <Tree
                      checkStatusAndToggleAddPage={checkStatusAndToggleAddPage}
                      createNewUnsavedPage={createNewUnsavedPage}
                      duplicatePage={checkStatusAndDuplicatePage}
                      isReadonly={isReadonlyFolder}
                      moveContent={moveContent}
                      showAddPageDialog={showAddPageDialog}
                      toggleAddPage={toggleAddPage}
                      visitFolder={visitFolder}
                      visitPage={checkStatusAndVisitPage}
                    />
                  </div>
                )}
              </Collapse>
              <div className={styles.divider} />
              <div
                className={classnames(styles.componentHeader, 'p-4')}
                onClick={this.handleComponentsTemplateClick}
                data-test-id="explorer-components-button"
              >
                Components
                <Arrow
                  facing={applicationMode === COMPONENT_TEMPLATE ? 'down' : 'right'}
                  color="white"
                  theme={styles.toggleArrow}
                />
              </div>
              <Collapse isOpen={applicationMode === COMPONENT_TEMPLATE}>
                {applicationMode === COMPONENT_TEMPLATE && (
                  <div className={classnames(styles.componentsWrapper, 'p-4')}>
                    <ComponentsTab
                      addNewComponentToPage={addNewComponentToPage}
                      canEditAtLeastOneComponent={canEditAtLeastOneComponent}
                      checkStatusAndToggleCreateMasterComponent={
                        checkStatusAndToggleCreateMasterComponent
                      }
                      componentsList={editableComponentsList}
                      isPageSelected={isPageSelected}
                      isReadonlyFolder={isReadonlyFolder}
                      pageTemplateName={pageTemplateName}
                      pageType={pageType}
                      showCreateMasterComponentDialog={showCreateMasterComponentDialog}
                      toggleCreateMasterComponent={toggleCreateMasterComponent}
                    />
                  </div>
                )}
              </Collapse>
              <div className={styles.divider} />
              <div
                className={classnames(styles.searchHeader, 'p-4')}
                data-test-id="explorer-search-button"
                onClick={this.handleSearchTemplateClick}
              >
                Search
                <Arrow
                  facing={applicationMode === SEARCH_TEMPLATE ? 'down' : 'right'}
                  color="white"
                  theme={styles.toggleArrow}
                />
              </div>
              <Collapse
                className={classnames('overflow-y', styles.details)}
                isOpen={applicationMode === SEARCH_TEMPLATE}
              >
                <div className={styles.divider} />
                {applicationMode === SEARCH_TEMPLATE && (
                  <SearchTab
                    addFilter={addFilter}
                    checkStatusAndVisitPage={checkStatusAndVisitPage}
                    componentsList={
                      isEmpty(editableComponentsList)
                        ? Object.keys(contractMap).sort()
                        : editableComponentsList
                    }
                    createNewUnsavedPage={createNewUnsavedPage}
                    cwdSiteRoot={cwdSiteRoot}
                    deleteFilter={deleteFilter}
                    duplicatePage={checkStatusAndDuplicatePage}
                    filters={filters}
                    getSearchResults={getSearchResults}
                  />
                )}
              </Collapse>
            </Fragment>
          )}
        </div>
      </div>
    );
  }
}
